import { useCallback, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";

import { mergeMilestone } from "../../../../../services/apiEpic";
import { useResponsive } from "../../../../../hooks/useResponsive";
import useToastPromise from "../../../../../hooks/useToast";
import useBoardIdByKey from "../../../../../hooks/useBoardIdByKey";
import { getResponseData } from "../../../../../reactQuery/response";

export function useMergeMilestoneState({
  close,
  unlockedMilestone,
  isSelectSourceMilestone,
  selectedSourceMilestone,
}) {
  const queryClient = useQueryClient();
  const isMobile = useResponsive("down", "md");
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentMilestone, setCurrentMilestone] = useState(null);
  const [selectedTargetMilestone, setSelectedTargetMilestone] = useState(null);
  const { showToastPromise } = useToastPromise();
  const { id } = useBoardIdByKey();
  const methods = useForm({
    defaultValues: {
      currentMilestone: "",
      targetMilestone: "",
    },
  });

  const sourceMilestoneData = useMemo(
    () =>
      getResponseData(
        selectedSourceMilestone,
        selectedSourceMilestone?.epic ? selectedSourceMilestone : {}
      ),
    [selectedSourceMilestone]
  );
  const epicDetail = sourceMilestoneData?.epic;
  const availableMilestones = useMemo(() => unlockedMilestone || [], [unlockedMilestone]);
  const activeCurrentMilestone =
    currentMilestone ?? (isSelectSourceMilestone ? epicDetail?.epic_id : null);
  const targetMilestoneOptions = useMemo(
    () =>
      availableMilestones.filter((item) => item.epic_id !== activeCurrentMilestone),
    [activeCurrentMilestone, availableMilestones]
  );
  const sourceMilestoneOptions = useMemo(
    () =>
      availableMilestones.filter((item) => item.epic_id !== selectedTargetMilestone),
    [availableMilestones, selectedTargetMilestone]
  );

  const { mutate, isPending } = useMutation({
    mutationFn: async (data) => showToastPromise(mergeMilestone, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["cards", id],
      });
      queryClient.invalidateQueries({
        queryKey: ["epic", id],
      });

      if (selectedTargetMilestone != null) {
        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.set("milestone", selectedTargetMilestone);
        setSearchParams(nextSearchParams);
      }

      close(false);
    },
  });

  const handleClose = useCallback(() => {
    close(false);
  }, [close]);

  const handleSubmit = useCallback(
    (data) => {
      if (isSelectSourceMilestone) {
        mutate({
          currentMilestone: epicDetail?.epic_id,
          targetMilestone: data?.targetMilestone,
        });
        return;
      }

      mutate(data);
    },
    [epicDetail?.epic_id, isSelectSourceMilestone, mutate]
  );

  return {
    currentMilestone: activeCurrentMilestone,
    epicDetail,
    handleClose,
    handleSubmit,
    isMobile,
    isPending,
    isSubmitDisabled:
      activeCurrentMilestone == null || selectedTargetMilestone == null,
    methods,
    selectedTargetMilestone,
    setCurrentMilestone,
    setSelectedTargetMilestone,
    sourceMilestoneOptions,
    targetMilestoneOptions,
  };
}
